import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import type { Content } from '@/api/types/stowed-element'
import { resolveEffectivePhotoFillNaturalSize } from '@/lib/photos/photo-fill-resolution'
import { rotateVector } from '@/lib/transforms/geometry'

/**
 * Resolves a photo fill's actual rendered size for `content.image.base`, matching the legacy
 * app's `<IM W H>` (the image's real displayed size). This is NOT optional: when `W`/`H` are
 * absent, legacy's `ODImage` constructor sets them to `0`, and its renderer then falls back to
 * `naturalPixelSize * 72/300` (a fixed 300dpi conversion, unrelated to the shape's frame —
 * `SvgUtil.createSvgImage`, odeditorsvg.js:16965-16971) — producing a zoomed-in, arbitrarily
 * cropped fraction of the photo instead of a fit to the shape. Confirmed by direct testing.
 *
 * Always recomputes from the shape's *current* width/height and `fill.scale` — never returns
 * `persistedRenderedWidth/Height` as-is. Those are a stale absolute size from whenever natural
 * size last successfully resolved; if the shape gets resized again before the next resolution
 * completes (a real, easy-to-hit race — see `resolveEffectivePhotoFillNaturalSize`), saving that
 * stale value directly writes a size that reflects the *previous* frame, not the one just
 * committed — exactly "legacy (and a fresh reload of our own app) shows a small, stale photo
 * inside the new, larger frame" once naturalWidth/Height genuinely aren't known yet.
 * `resolveEffectivePhotoFillNaturalSize`'s ratio-equivalent proxy keeps the cover-fit math
 * correct either way, always relative to the shape's actual current size.
 */
export function resolvePhotoFillRenderedSize(
  fill: ShapePhotoFill,
  shape: ShapeData
): { width: number; height: number } {
  const { naturalWidth, naturalHeight } = resolveEffectivePhotoFillNaturalSize(fill)

  if (naturalWidth && naturalHeight) {
    const coverScale = Math.max(shape.width / naturalWidth, shape.height / naturalHeight)

    return {
      width: naturalWidth * coverScale * fill.scale,
      height: naturalHeight * coverScale * fill.scale,
    }
  }

  return { width: shape.width, height: shape.height }
}

/**
 * Builds `content.image` for a photo fill placed inside a shape (kindId 1, 2, 9 closed, 10).
 * Mirrors the legacy app's `<XX><IM X Y W H R II IV pezid/></XX>` — `X`/`Y` are offsets from the
 * shape's own center, `W`/`H` are the actual rendered size. A negative `W`/`H` is legacy's own
 * encoding of a horizontally/vertically-flipped image (`ODImage.flipx`/`.flipy`), reproduced here
 * for `fill.flippedX`/`fill.flippedY`. Geometry fields are flat on `image` itself (no nested
 * `base`) — matches the backend's `ImageElement` Go struct.
 *
 * `X`/`Y` are NOT simply `offsetX`/`offsetY` when `rotation` is nonzero. Legacy
 * (`SvgUtil.getSvgTransform`, odeditorsvg.js) positions the image at `X`/`Y` first and then
 * rotates it **around that same point** (the image's own, already-panned center) — panning never
 * moves relative to rotation. Larry's own crop-mode renderer (`photo-fill-transform.ts`) instead
 * rotates around the frame's fixed center, wrapping the pan translate — so panning then rotating
 * actually swings the image to a new position. The two conventions only agree when the pan
 * offset is zero. To reproduce Larry's swung position under legacy's in-place rotation, the pan
 * offset must be pre-rotated by `rotation` before it's written as `X`/`Y`. The inverse rotation
 * is applied on load — see `readShapeFillImage` in checkpoint-to-shapes.ts.
 */
export function buildShapeImageFillContent(shape: ShapeData): Content | undefined {
  const fill = shape.photoFill

  if (!fill) return undefined

  const { width, height } = resolvePhotoFillRenderedSize(fill, shape)
  const wireWidth = fill.flippedX ? -width : width
  const wireHeight = fill.flippedY ? -height : height
  const rotation = fill.rotation ?? 0
  const wireOffset = rotateVector({ x: fill.offsetX, y: fill.offsetY }, rotation)

  return {
    image: {
      x: wireOffset.x,
      y: wireOffset.y,
      // Legacy's own convention for a horizontally/vertically-flipped image
      // (`ODImage.flipx`/`.flipy`): a negative width/height, not a dedicated flag — see
      // `ShapePhotoFill.flippedX`/`.flippedY` doc comments.
      width: wireWidth,
      height: wireHeight,
      rotation,
      imageId: fill.imageId,
      imageType: fill.imageType,
      ...(fill.pezId ? { pezId: fill.pezId } : {}),
      ...(fill.previewUrl ? { imagePreviewUrl: fill.previewUrl } : {}),
      ...(fill.displayName ? { imageDisplayName: fill.displayName } : {}),
    },
  }
}
